<script lang="ts">
  import { addNotification } from "../lib/stores";
  import Layout from "../components/layout/Layout.svelte";

  let { user = null }: {
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  // Notification toggles
  let scheduleAlert = $state(true);
  let expenseAlert = $state(true);
  let inviteAlert = $state(true);
  let settlementAlert = $state(true);
  let systemAlert = $state(false);

  // Quiet hours
  let quietHoursEnabled = $state(false);
  let quietStart = $state("22:00");
  let quietEnd = $state("08:00");

  const categories = [
    { key: "scheduleAlert", label: "일정 알림", desc: "다가오는 일정을 미리 알려드립니다", icon: "📅" },
    { key: "expenseAlert", label: "경비 알림", desc: "새 경비가 등록되거나 수정될 때 알림을 받습니다", icon: "💰" },
    { key: "inviteAlert", label: "초대 알림", desc: "여행 초대 요청을 받을 때 알림을 받습니다", icon: "✈️" },
    { key: "settlementAlert", label: "정산 알림", desc: "정산 완료 및 송금 요청 알림을 받습니다", icon: "💸" },
    { key: "systemAlert", label: "시스템 알림", desc: "앱 업데이트 및 공지사항 알림을 받습니다", icon: "🔔" },
  ] as const;

  function getToggleValue(key: string): boolean {
    const map: Record<string, boolean> = {
      scheduleAlert, expenseAlert, inviteAlert, settlementAlert, systemAlert
    };
    return map[key] ?? false;
  }

  function toggleSetting(key: string) {
    if (key === "scheduleAlert") scheduleAlert = !scheduleAlert;
    else if (key === "expenseAlert") expenseAlert = !expenseAlert;
    else if (key === "inviteAlert") inviteAlert = !inviteAlert;
    else if (key === "settlementAlert") settlementAlert = !settlementAlert;
    else if (key === "systemAlert") systemAlert = !systemAlert;
  }

  function saveSettings() {
    // TODO: persist to server or localStorage
    addNotification("success", "알림 설정이 저장되었습니다.");
  }
</script>

<Layout {user}>
  <div class="max-w-2xl mx-auto px-4 py-8 space-y-6">
    <!-- Header -->
    <div class="flex items-center gap-3">
      <a href="/settings" aria-label="설정으로 돌아가기" class="flex items-center justify-center w-9 h-9 rounded-xl transition-colors" style="background: var(--surface); border: 1px solid var(--border);">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M11 14L6 9L11 4" stroke="rgba(255,255,255,0.6)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </a>
      <div>
        <h1 class="text-2xl font-bold" style="color: var(--text-primary);">알림 설정</h1>
        <p class="text-sm mt-0.5" style="color: var(--text-muted);">받고 싶은 알림을 선택하세요</p>
      </div>
    </div>

    <!-- Category Toggles -->
    <section class="rounded-2xl overflow-hidden" style="background: var(--surface); border: 1px solid var(--border);">
      <div class="px-5 py-4" style="border-bottom: 1px solid var(--border);">
        <h2 class="text-base font-semibold" style="color: var(--text-primary);">알림 카테고리</h2>
      </div>
      {#each categories as cat, i}
        <div
          class="flex items-center gap-4 px-5 py-4"
          style="{i < categories.length - 1 ? 'border-bottom: 1px solid var(--border);' : ''}"
        >
          <div class="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style="background: rgba(255,255,255,0.04);">
            {cat.icon}
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium" style="color: var(--text-primary);">{cat.label}</p>
            <p class="text-xs mt-0.5" style="color: var(--text-muted);">{cat.desc}</p>
          </div>
          <button
            onclick={() => toggleSetting(cat.key)}
            role="switch"
            aria-checked={getToggleValue(cat.key)}
            aria-label={cat.label}
            class="relative w-12 h-6 rounded-full transition-colors flex-shrink-0"
            style="background: {getToggleValue(cat.key) ? 'var(--accent)' : 'rgba(255,255,255,0.1)'};"
          >
            <span
              class="absolute top-0.5 w-5 h-5 rounded-full shadow transition-transform"
              style="background: white; transform: translateX({getToggleValue(cat.key) ? '26px' : '2px'});"
            ></span>
          </button>
        </div>
      {/each}
    </section>

    <!-- Quiet Hours -->
    <section class="rounded-2xl p-5 space-y-4" style="background: var(--surface); border: 1px solid var(--border);">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-base font-semibold" style="color: var(--text-primary);">방해금지 모드</h2>
          <p class="text-xs mt-0.5" style="color: var(--text-muted);">설정한 시간 동안 알림을 받지 않습니다</p>
        </div>
        <button
          onclick={() => quietHoursEnabled = !quietHoursEnabled}
          role="switch"
          aria-checked={quietHoursEnabled}
          aria-label="방해금지 모드"
          class="relative w-12 h-6 rounded-full transition-colors flex-shrink-0"
          style="background: {quietHoursEnabled ? 'var(--accent)' : 'rgba(255,255,255,0.1)'};"
        >
          <span
            class="absolute top-0.5 w-5 h-5 rounded-full shadow transition-transform"
            style="background: white; transform: translateX({quietHoursEnabled ? '26px' : '2px'});"
          ></span>
        </button>
      </div>

      {#if quietHoursEnabled}
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label for="quiet-start" class="block text-xs font-medium mb-1.5" style="color: var(--text-muted);">시작 시간</label>
            <input
              id="quiet-start"
              type="time"
              bind:value={quietStart}
              class="w-full px-4 py-3 rounded-xl text-sm outline-none"
              style="background: rgba(255,255,255,0.05); color: var(--text-primary); border: 1px solid var(--border);"
            />
          </div>
          <div>
            <label for="quiet-end" class="block text-xs font-medium mb-1.5" style="color: var(--text-muted);">종료 시간</label>
            <input
              id="quiet-end"
              type="time"
              bind:value={quietEnd}
              class="w-full px-4 py-3 rounded-xl text-sm outline-none"
              style="background: rgba(255,255,255,0.05); color: var(--text-primary); border: 1px solid var(--border);"
            />
          </div>
        </div>
      {/if}
    </section>

    <!-- Save -->
    <button
      onclick={saveSettings}
      class="w-full py-3.5 rounded-2xl text-sm font-bold transition-colors"
      style="background: var(--accent); color: #000;"
    >
      설정 저장
    </button>
  </div>
</Layout>
